import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SbButton from '@Components/Button';
import { CancelledShipment, DiscrepancyResponseData } from './types';

type Props = {
  open: boolean;
  data: DiscrepancyResponseData | any;
  onClose: () => void;
  onCreateInvoice: () => void;
  onCancelWholeBatch?: () => void;
  loading?: boolean;
};

const renderValue = (value?: string | number | null): string => {
  if (value === null || value === undefined) return '—';
  const str = String(value).trim();
  return str !== '' ? str : '—';
};

export default function DiscrepancyModal({
  open,
  data,
  onClose,
  onCreateInvoice,
  onCancelWholeBatch,
  loading = false,
}: Props) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!data?.reportUrl) return;

    setDownloading(true);
    try {
      window.open(data.reportUrl, '_blank');
    } catch (error) {
      console.error('Error downloading report:', error);
    } finally {
      setDownloading(false);
    }
  };

  // Check if reportUrl exists and is not null
  const hasDiscrepancies = data?.reportUrl !== null && data?.reportUrl !== undefined;

  // Cancelled shipments data handling
  const cancelledShipments: CancelledShipment[] = Array.isArray(data?.cancelledShipments)
    ? data.cancelledShipments
    : [];

  const cancelledCount: number =
    typeof data?.cancelledShipmentCount === 'number' && !isNaN(data.cancelledShipmentCount)
      ? data.cancelledShipmentCount
      : cancelledShipments.length;

  const hasCancelledShipments = cancelledCount > 0 || cancelledShipments.length > 0;
  const hasIssues = hasDiscrepancies || hasCancelledShipments;

  if (!data) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {hasDiscrepancies ? (
          <>
            <WarningIcon color="warning" />
            Shipment Charges Discrepancy Found
          </>
        ) : hasCancelledShipments ? (
          <>
            <WarningIcon color="warning" />
            Cancelled Shipments Detected
          </>
        ) : (
          <>
            <CheckCircleIcon color="success" />
            No Discrepancies Found
          </>
        )}
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {hasDiscrepancies
            ? 'We found some discrepancies between your uploaded data and our records. Please review the report below.'
            : hasCancelledShipments
            ? 'We found cancelled shipments in your uploaded data. Please review the details below.'
            : 'All shipment charges match our records. You can proceed with invoice creation.'}
        </Typography>

        {hasDiscrepancies && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Discrepancies found
            </Typography>
            <Typography variant="body2">
              Some entries in your uploaded sheet contain values that differ from our shipment data. You can either
              review the mismatches or continue with invoice creation.
            </Typography>
          </Alert>
        )}

        {!hasIssues && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              All charges match perfectly. You can safely proceed with invoice creation.
            </Typography>
          </Alert>
        )}

        {/* Cancelled Shipments Section */}
        {hasCancelledShipments && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                Cancelled Shipments
              </Typography>
              <Chip
                label={cancelledCount}
                size="small"
                color="error"
                variant="outlined"
                sx={{ fontWeight: 600, height: 22, minWidth: 22, fontSize: '0.75rem' }}
              />
            </Box>

            {cancelledShipments.length > 0 ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  maxHeight: 260,
                  borderRadius: 2,
                  overflow: 'auto',
                  backgroundColor: 'background.paper',
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'action.hover' }}>Shipment Number</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'action.hover' }}>AWB Number</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'action.hover' }}>
                        Carrier Reference Number
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'action.hover', textAlign: 'center' }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cancelledShipments.map((shipment, index) => {
                      const statusLabel = renderValue(shipment.status) !== '—' ? String(shipment.status) : 'Cancelled';

                      return (
                        <TableRow key={shipment.shipmentNumber || shipment.awbNumber || `cancelled-${index}`} hover>
                          <TableCell sx={{ wordBreak: 'break-word', fontWeight: 500 }}>
                            {renderValue(shipment.shipmentNumber)}
                          </TableCell>
                          <TableCell sx={{ wordBreak: 'break-word' }}>{renderValue(shipment.awbNumber)}</TableCell>
                          <TableCell sx={{ wordBreak: 'break-word' }}>
                            {renderValue(shipment.carrierReferenceNumber)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Chip
                              label={statusLabel}
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ textTransform: 'capitalize', fontWeight: 500 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  {cancelledCount === 1
                    ? '1 cancelled shipment was identified.'
                    : `${cancelledCount} cancelled shipments were identified.`}
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* Download Report Section - Only show if reportUrl exists */}
        {hasDiscrepancies && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <SbButton
              variant="outlined"
              startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleDownload}
              disabled={downloading}
              sx={{ mb: 1 }}
            >
              {downloading ? 'Downloading...' : 'Download Discrepancy Report'}
            </SbButton>
            <Typography variant="caption" display="block" color="textSecondary">
              Download the complete discrepancy report for detailed analysis
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        {onCancelWholeBatch && (
          <SbButton
            onClick={onCancelWholeBatch}
            color="error"
            variant="outlined"
            disabled={loading || downloading}
            sx={{ mr: 'auto' }}
          >
            Cancel Whole Batch
          </SbButton>
        )}
        <SbButton onClick={onClose} disabled={loading || downloading}>
          {hasIssues ? 'Review Manually' : 'Close'}
        </SbButton>
        <SbButton
          onClick={onCreateInvoice}
          variant="contained"
          color={hasIssues ? 'warning' : 'primary'}
          disabled={loading || downloading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {loading ? 'Processing...' : hasIssues ? 'Move Ahead' : 'Create Invoice'}
        </SbButton>
      </DialogActions>
    </Dialog>
  );
}
